/**
 * DOM helpers exposed to Blazor via JSInterop, e.g.
 * await JSRuntime.InvokeVoidAsync("domUtils.scrollToElement", "elementId");
 */

export const domUtils = {
  scrollToElement(elementId: string, behavior: ScrollBehavior = 'smooth'): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior, block: 'start' });
    } else {
      console.warn(`Element with ID "${elementId}" not found`);
    }
  },

  scrollToTop(behavior: ScrollBehavior = 'smooth'): void {
    window.scrollTo({ top: 0, behavior });
  },

  scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    window.scrollTo({ top: document.body.scrollHeight, behavior });
  },

  getBoundingRect(elementId: string): DOMRect | null {
    const element = document.getElementById(elementId);
    return element ? element.getBoundingClientRect() : null;
  },

  getElementHeight(elementId: string): number {
    const element = document.getElementById(elementId);
    return element ? element.offsetHeight : 0;
  },

  getElementWidth(elementId: string): number {
    const element = document.getElementById(elementId);
    return element ? element.offsetWidth : 0;
  },

  isElementInViewport(elementId: string): boolean {
    const element = document.getElementById(elementId);
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  },

  focusElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element && element instanceof HTMLElement) {
      element.focus();
    }
  },

  blurElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element && element instanceof HTMLElement) {
      element.blur();
    }
  },

  addClass(elementId: string, className: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.classList.add(className);
    }
  },

  removeClass(elementId: string, className: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.classList.remove(className);
    }
  },

  toggleClass(elementId: string, className: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.classList.toggle(className);
    }
  },

  hasClass(elementId: string, className: string): boolean {
    const element = document.getElementById(elementId);
    return element ? element.classList.contains(className) : false;
  },

  getWindowSize(): { width: number; height: number } {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    };
  },

  getScrollPosition(): { x: number; y: number } {
    return {
      x: window.pageXOffset || document.documentElement.scrollLeft,
      y: window.pageYOffset || document.documentElement.scrollTop
    };
  }
};

if (typeof window !== 'undefined') {
  (window as any).domUtils = domUtils;
}
